import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import CookieBanner from '../../components/feature/CookieBanner';
import BackToTopButton from '../../components/feature/BackToTopButton';

const HomePage = () => {
  useEffect(() => {
    document.title = 'Instalación de Pladur en Mataró | PLADUREX - Profesionales en Pladur';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Instalación de pladur en Mataró: tabiques, trasdosados, falsos techos y aislamientos. Obra limpia y rápida. Presupuesto sin compromiso ☎ 629 501 941');
    }

    // Add WebPage Schema.org
    const existingWebPageSchema = document.querySelector('script[data-schema="webpage"]');
    if (existingWebPageSchema) existingWebPageSchema.remove();
    
    const webPageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${import.meta.env.VITE_SITE_URL}/#webpage`,
      "url": import.meta.env.VITE_SITE_URL,
      "name": "Instalación de Pladur en Mataró | PLADUREX - Profesionales en Pladur",
      "description": "Instalación de pladur en Mataró: tabiques, trasdosados, falsos techos y aislamientos. Obra limpia y rápida. Presupuesto sin compromiso ☎ 629 501 941",
      "inLanguage": "es-ES",
      "isPartOf": {
        "@id": `${import.meta.env.VITE_SITE_URL}/#website`
      },
      "about": {
        "@id": `${import.meta.env.VITE_SITE_URL}/#organization`
      },
      "primaryImageOfPage": {
        "@type": "ImageObject",
        "url": "https://public.readdy.ai/ai/img_res/52acd383252acd4599dd59b1d9258165.jpg"
      }
    };
    
    const webPageScript = document.createElement('script');
    webPageScript.type = 'application/ld+json';
    webPageScript.setAttribute('data-schema', 'webpage');
    webPageScript.textContent = JSON.stringify(webPageSchema);
    document.head.appendChild(webPageScript);

    // Add BreadcrumbList Schema.org
    const existingSchema = document.querySelector('script[data-schema="breadcrumb"]');
    if (existingSchema) existingSchema.remove();
    
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Inicio",
          "item": import.meta.env.VITE_SITE_URL
        }
      ]
    };
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'breadcrumb');
    script.textContent = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(script);

    return () => {
      const schemaScript = document.querySelector('script[data-schema="breadcrumb"]');
      if (schemaScript) schemaScript.remove();
      const webPageSchemaScript = document.querySelector('script[data-schema="webpage"]');
      if (webPageSchemaScript) webPageSchemaScript.remove();
    };
  }, []);

  const services = [
    {
      icon: 'ri-layout-grid-line',
      title: 'Tabiques de Pladur',
      description: 'Divisiones interiores rápidas y limpias para redistribuir espacios en viviendas, oficinas y locales.',
      link: '/servicios#tabiques'
    },
    {
      icon: 'ri-building-line',
      title: 'Trasdosados',
      description: 'Directos y autoportantes con opciones de aislamiento térmico y acústico para mayor confort.',
      link: '/servicios#trasdosados'
    },
    {
      icon: 'ri-stack-line',
      title: 'Falsos Techos',
      description: 'Techos continuos y registrables que ocultan instalaciones y mejoran la estética de cualquier espacio.',
      link: '/servicios#techos'
    },
    {
      icon: 'ri-volume-mute-line',
      title: 'Aislamiento Acústico',
      description: 'Soluciones de insonorización para reducir el ruido entre estancias y mejorar el confort.',
      link: '/servicios#acustico'
    },
    {
      icon: 'ri-temp-cold-line',
      title: 'Aislamiento Térmico',
      description: 'Mejora la eficiencia energética de tu hogar u oficina con nuestros sistemas de aislamiento.',
      link: '/servicios#termico'
    },
    {
      icon: 'ri-drop-line',
      title: 'Pladur Antihumedad',
      description: 'Placas especiales para baños, cocinas y lavaderos que resisten la humedad y el vapor.',
      link: '/servicios#antihumedad'
    }
  ];

  const benefits = [
    { icon: 'ri-time-line', text: 'Obra rápida y sin escombros' },
    { icon: 'ri-checkbox-circle-line', text: 'Acabados limpios y profesionales' },
    { icon: 'ri-sound-module-line', text: 'Mejora acústica y térmica' },
    { icon: 'ri-tools-line', text: 'Redistribución flexible de espacios' }
  ];

  const testimonials = [
    {
      name: 'María García',
      location: 'Centre, Mataró',
      rating: 5,
      text: 'Excelente trabajo en la instalación de tabiques para dividir el salón. Obra limpia, rápida y el acabado quedó perfecto. Muy profesionales.',
      service: 'Tabiques de Pladur'
    },
    {
      name: 'Jordi Puig',
      location: 'Cerdanyola, Mataró',
      rating: 5,
      text: 'Instalaron el falso techo en mi oficina en solo dos días. El resultado es impecable y ahora tenemos mucho mejor aislamiento acústico.',
      service: 'Falsos Techos'
    },
    {
      name: 'Ana Martínez',
      location: 'Rocafonda, Mataró',
      rating: 5,
      text: 'Muy contentos con el trasdosado con aislamiento térmico. Se nota la diferencia en la factura de la calefacción. Totalmente recomendables.',
      service: 'Trasdosados'
    },
    {
      name: 'Carlos Fernández',
      location: 'Argentona',
      rating: 5,
      text: 'Reformaron el baño con pladur antihumedad y quedó espectacular. Cumplieron con los plazos y dejaron todo limpio. Volveré a contar con ellos.',
      service: 'Pladur Antihumedad'
    }
  ];

  const process = [
    { step: '01', title: 'Contacto', description: 'Llámanos o escríbenos por WhatsApp' },
    { step: '02', title: 'Visita', description: 'Medición y análisis de tu proyecto' },
    { step: '03', title: 'Presupuesto', description: 'Propuesta detallada sin compromiso' },
    { step: '04', title: 'Ejecución', description: 'Instalación profesional y limpia' },
    { step: '05', title: 'Entrega', description: 'Remates finales listos para pintar' }
  ];

  const neighborhoods = [
    'Centre', 'Eixample', 'Cerdanyola', 'Rocafonda', 'El Palau', 
    'Vista Alegre', 'Cirera', 'Peramàs', 'Pla d\'en Boet', 'L\'Havana'
  ];

  const faqs = [
    {
      question: '¿Cuánto tiempo tarda una instalación de pladur?',
      answer: 'Los plazos varían según el tipo de trabajo. Un tabique estándar de una habitación se puede instalar en 1-2 días. Los trasdosados de una estancia tardan entre 2-3 días. Los falsos techos dependen de los m² y la complejidad, pero una habitación media se completa en 2-3 días. Las reformas completas se planifican según el alcance del proyecto.'
    },
    {
      question: '¿El pladur aísla del ruido realmente?',
      answer: 'Sí, el pladur con el sistema adecuado ofrece excelente aislamiento acústico. Para conseguir buenos resultados se utiliza doble placa, lana de roca de alta densidad entre las placas, perfiles antivibratorios y banda elástica perimetral. Un sistema bien ejecutado puede reducir el ruido entre 45-55 dB, suficiente para aislar dormitorios, salas de música o zonas de trabajo.'
    },
    {
      question: '¿Se puede usar pladur en baños y cocinas?',
      answer: 'Sí, para zonas húmedas utilizamos placas de pladur antihumedad (WA) que tienen un núcleo tratado que resiste la humedad y el vapor. Estas placas son perfectas para baños, cocinas, lavaderos y cualquier zona con alta humedad ambiental. Se pueden alicatar o pintar sin problemas.'
    },
    {
      question: '¿Qué incluye el presupuesto?',
      answer: 'Nuestros presupuestos incluyen todo lo necesario: materiales (placas, perfilería, aislantes, pasta de juntas), mano de obra cualificada, transporte, montaje, tratamiento de juntas, acabados y limpieza. Te entregamos el trabajo listo para pintar. No hay costes ocultos ni sorpresas.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0B0B0F] text-[#F3F3F3]">
      <Header />
      <WhatsAppButton />

      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=modern%20minimalist%20interior%20construction%20workspace%20with%20clean%20white%20walls%20and%20professional%20tools%20in%20dark%20atmospheric%20lighting%20with%20subtle%20fuchsia%20accent%20glow%20perfect%20for%20renovation%20and%20drywall%20installation%20professional%20photography&width=1920&height=1080&seq=hero-bg-001&orientation=landscape"
            alt="Instalación profesional de pladur en Mataró"
            className="w-full h-full object-cover object-top"
            loading="eager"
            fetchPriority="high"
            width="1920"
            height="1080"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-[#0B0B0F]"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight break-words">
              <strong>Instalación de pladur en Mataró</strong>
              <span className="block mt-2 text-[#FF007A]">Obra limpia y profesional</span>
            </h1>
            <p className="text-lg sm:text-xl text-[#F3F3F3]/90 mb-8 max-w-3xl mx-auto leading-relaxed break-words">
              Especialistas en <strong>instalación de pladur en Mataró</strong> y comarca del Maresme. Tabiques, trasdosados, falsos techos y aislamientos con acabados impecables. Solución rápida para tu reforma.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="tel:+34629501941"
                className="px-8 py-4 bg-[#FF007A] text-white rounded-lg text-base font-semibold hover:bg-[#FF4DA6] transition-all duration-300 shadow-lg hover:shadow-[#FF007A]/50 whitespace-nowrap cursor-pointer"
                aria-label="Llamar a PLADUREX al 629 501 941"
              >
                <i className="ri-phone-line mr-2" aria-hidden="true"></i>
                Llamar Ahora
              </a>
              <a
                href="https://wa.me/34629501941"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-lg text-base font-semibold hover:bg-white/20 transition-all duration-300 border border-white/20 whitespace-nowrap cursor-pointer"
                aria-label="Contactar por WhatsApp"
              >
                <i className="ri-whatsapp-line mr-2" aria-hidden="true"></i>
                WhatsApp
              </a>
            </div>
          </motion.div>
        </div>

        <motion.div
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          aria-hidden="true"
        >
          <i className="ri-arrow-down-line text-3xl text-[#FF007A]"></i>
        </motion.div>
      </section>

      <section className="py-20 bg-[#0B0B0F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 break-words">
              Nuestros <span className="text-[#FF007A]">servicios</span>
            </h2>
            <p className="text-lg text-[#F3F3F3]/70 max-w-2xl mx-auto break-words">
              <a href="https://sabadell.pladures.es/" target="_blank" rel="noopener noreferrer" className="hover:text-[#FF007A] transition-colors cursor-pointer">Soluciones completas de pladur para viviendas</a>, oficinas y locales comerciales en Mataró y el Maresme
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <Link
                  to={service.link}
                  className="block h-full bg-[#12121A] rounded-xl p-6 border border-[#FF007A]/10 hover:border-[#FF007A]/30 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-14 h-14 bg-[#FF007A]/10 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${service.icon} text-2xl text-[#FF007A]`} aria-hidden="true"></i>
                  </div>
                  <h3 className="text-xl font-semibold mb-3 break-words">{service.title}</h3>
                  <p className="text-[#F3F3F3]/70 text-sm leading-relaxed break-words">{service.description}</p>
                </Link>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link
              to="/servicios"
              className="inline-block px-8 py-3 bg-[#FF007A] text-white rounded-lg font-semibold hover:bg-[#FF4DA6] transition-all duration-300 shadow-lg hover:shadow-[#FF007A]/50 whitespace-nowrap cursor-pointer"
            >
              Ver Todos los Servicios
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-[#12121A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold mb-6 break-words">
                ¿Por qué elegir <span className="text-[#FF007A]">pladur?</span>
              </h2>
              <p className="text-[#F3F3F3]/70 mb-8 leading-relaxed break-words">
                El pladur es la solución ideal para reformas modernas. Permite redistribuir espacios sin obras pesadas, mejora el <Link to="/servicios#acustico" className="text-[#FF007A] hover:underline">aislamiento acústico</Link> y <Link to="/servicios#termico" className="text-[#FF007A] hover:underline">térmico</Link>, y ofrece acabados perfectos listos para pintar en tiempo récord. <Link to="/empresa" className="text-[#FF007A] hover:underline">Conoce más sobre nuestro equipo</Link>.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start"
                  >
                    <div className="w-10 h-10 bg-[#FF007A]/10 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                      <i className={`${benefit.icon} text-lg text-[#FF007A]`} aria-hidden="true"></i>
                    </div>
                    <p className="text-sm text-[#F3F3F3]/80 pt-2 break-words">{benefit.text}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative h-96 rounded-xl overflow-hidden w-full"
            >
              <img 
                src="https://readdy.ai/api/search-image?query=professional%20drywall%20installation%20interior%20with%20clean%20white%20walls%20modern%20tools%20and%20materials%20minimalist%20workspace%20with%20dark%20atmospheric%20lighting%20and%20subtle%20fuchsia%20accent%20perfect%20construction%20quality&width=800&height=600&seq=benefits-img-002&orientation=landscape"
                alt="Beneficios del pladur - instalación profesional"
                className="w-full h-full object-cover object-top"
                loading="lazy"
                width="800"
                height="600"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0B0B0F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 break-words">
              Lo que dicen <span className="text-[#FF007A]">nuestros clientes</span>
            </h2>
            <p className="text-lg text-[#F3F3F3]/70 max-w-2xl mx-auto break-words">
              La <a href="https://pladures.es/barcelona/mataro/" target="_blank" rel="noopener noreferrer" className="hover:text-[#FF007A] transition-colors cursor-pointer">satisfacción de nuestros clientes en Mataró y el Maresme</a> es nuestra mejor carta de presentación
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-[#12121A] rounded-xl p-6 border border-[#FF007A]/10 hover:border-[#FF007A]/30 transition-all duration-300"
              >
                <div className="flex items-center mb-4" role="img" aria-label={`Valoración: ${testimonial.rating} de 5 estrellas`}>
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-[#FF007A] text-sm mr-1" aria-hidden="true"></i>
                  ))}
                </div>
                <p className="text-[#F3F3F3]/80 text-sm leading-relaxed mb-4 italic break-words">
                  "{testimonial.text}"
                </p>
                <div className="border-t border-[#FF007A]/10 pt-4">
                  <p className="font-semibold text-[#F3F3F3]">{testimonial.name}</p>
                  <p className="text-xs text-[#F3F3F3]/50 mt-1">
                    <i className="ri-map-pin-line mr-1" aria-hidden="true"></i>
                    {testimonial.location}
                  </p>
                  <span className="inline-block mt-2 px-3 py-1 bg-[#FF007A]/10 text-[#FF007A] text-xs rounded-full">
                    {testimonial.service}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#12121A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 break-words">
              Garantías y <span className="text-[#FF007A]">certificaciones</span>
            </h2>
            <p className="text-lg text-[#F3F3F3]/70 max-w-2xl mx-auto break-words">
              Tu tranquilidad es nuestra prioridad. Trabajamos con los más altos estándares de calidad
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0 }}
              className="bg-[#0B0B0F] rounded-xl p-4 sm:p-6 text-center border border-[#FF007A]/10 hover:border-[#FF007A]/30 transition-all duration-300"
            >
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#FF007A]/10 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <i className="ri-shield-check-line text-2xl sm:text-3xl text-[#FF007A]" aria-hidden="true"></i>
              </div>
              <h3 className="text-xs sm:text-sm font-semibold mb-1 sm:mb-2 break-words">Garantía de Obra</h3>
              <p className="text-xs text-[#F3F3F3]/60 break-words">Garantizamos todos nuestros trabajos</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-[#0B0B0F] rounded-xl p-4 sm:p-6 text-center border border-[#FF007A]/10 hover:border-[#FF007A]/30 transition-all duration-300"
            >
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#FF007A]/10 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <i className="ri-file-list-3-line text-2xl sm:text-3xl text-[#FF007A]" aria-hidden="true"></i>
              </div>
              <h3 className="text-xs sm:text-sm font-semibold mb-1 sm:mb-2 break-words">Presupuesto Cerrado</h3>
              <p className="text-xs text-[#F3F3F3]/60 break-words">Sin sorpresas ni costes ocultos</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-[#0B0B0F] rounded-xl p-4 sm:p-6 text-center border border-[#FF007A]/10 hover:border-[#FF007A]/30 transition-all duration-300"
            >
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#FF007A]/10 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <i className="ri-award-line text-2xl sm:text-3xl text-[#FF007A]" aria-hidden="true"></i>
              </div>
              <h3 className="text-xs sm:text-sm font-semibold mb-1 sm:mb-2 break-words">Materiales Certificados</h3>
              <p className="text-xs text-[#F3F3F3]/60 break-words">Placas y perfiles de primera calidad</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-[#0B0B0F] rounded-xl p-4 sm:p-6 text-center border border-[#FF007A]/10 hover:border-[#FF007A]/30 transition-all duration-300"
            >
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#FF007A]/10 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <i className="ri-user-star-line text-2xl sm:text-3xl text-[#FF007A]" aria-hidden="true"></i>
              </div>
              <h3 className="text-xs sm:text-sm font-semibold mb-1 sm:mb-2 break-words">Profesionales Cualificados</h3>
              <p className="text-xs text-[#F3F3F3]/60 break-words">Equipo con experiencia demostrada</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="bg-[#0B0B0F] rounded-xl p-4 sm:p-6 text-center border border-[#FF007A]/10 hover:border-[#FF007A]/30 transition-all duration-300"
            >
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#FF007A]/10 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <i className="ri-recycle-line text-2xl sm:text-3xl text-[#FF007A]" aria-hidden="true"></i>
              </div>
              <h3 className="text-xs sm:text-sm font-semibold mb-1 sm:mb-2 break-words">Gestión de Residuos</h3>
              <p className="text-xs text-[#F3F3F3]/60 break-words">Limpieza y reciclaje responsable</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="bg-[#0B0B0F] rounded-xl p-4 sm:p-6 text-center border border-[#FF007A]/10 hover:border-[#FF007A]/30 transition-all duration-300"
            >
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#FF007A]/10 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <i className="ri-bill-line text-2xl sm:text-3xl text-[#FF007A]" aria-hidden="true"></i>
              </div>
              <h3 className="text-xs sm:text-sm font-semibold mb-1 sm:mb-2 break-words">Factura Legal</h3>
              <p className="text-xs text-[#F3F3F3]/60 break-words">Documentación completa y legal</p>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 bg-gradient-to-r from-[#FF007A]/10 to-[#FF4DA6]/10 rounded-xl p-6 sm:p-8 border border-[#FF007A]/20"
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 w-full md:w-auto">
                <div className="w-14 h-14 bg-[#FF007A]/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-verified-badge-line text-2xl text-[#FF007A]" aria-hidden="true"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-semibold mb-1 break-words">Compromiso de Satisfacción</h3>
                  <p className="text-sm text-[#F3F3F3]/70 break-words">No damos por terminado el trabajo hasta que estés 100% satisfecho con el resultado</p>
                </div>
              </div>
              <a
                href="tel:+34629501941"
                className="w-full md:w-auto px-6 py-3 bg-[#FF007A] text-white rounded-lg font-semibold hover:bg-[#FF4DA6] transition-all duration-300 shadow-lg hover:shadow-[#FF007A]/50 whitespace-nowrap cursor-pointer flex-shrink-0 text-center"
                aria-label="Solicitar presupuesto llamando al 629 501 941"
              >
                <i className="ri-phone-line mr-2" aria-hidden="true"></i>
                Solicitar Presupuesto
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-[#12121A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 break-words">
              Cómo <span className="text-[#FF007A]">trabajamos</span>
            </h2>
            <p className="text-lg text-[#F3F3F3]/70 max-w-2xl mx-auto break-words">
              Proceso simple y transparente desde el primer contacto hasta la entrega final
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {process.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-[#FF007A]/10 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-[#FF007A]">
                  <span className="text-2xl font-bold text-[#FF007A]">{item.step}</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-[#F3F3F3]/70">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#12121A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 break-words">
              Zonas de <span className="text-[#FF007A]">servicio</span>
            </h2>
            <p className="text-lg text-[#F3F3F3]/70 mb-8 break-words">
              Instalación de pladur en Mataró y toda la comarca del Maresme
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4 mb-8">
            {neighborhoods.map((neighborhood, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-[#0B0B0F] rounded-lg p-3 sm:p-4 text-center border border-[#FF007A]/10"
              >
                <i className="ri-map-pin-line text-[#FF007A] text-lg sm:text-xl mb-2" aria-hidden="true"></i>
                <p className="text-xs sm:text-sm font-medium break-words">{neighborhood}</p>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-[#F3F3F3]/70 text-sm sm:text-base break-words px-4">
            También trabajamos en: <strong>Argentona, Cabrera de Mar, Vilassar de Mar, Premià de Mar, Dosrius, Arenys</strong> y <strong>Barcelona</strong> (Sant Martí, Eixample)
          </p>
        </div>
      </section>

      <section className="py-20 bg-[#0B0B0F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 break-words">
              Mapa de <span className="text-[#FF007A]">cobertura</span>
            </h2>
            <p className="text-lg text-[#F3F3F3]/70 mb-8 break-words">
              Servicio profesional de pladur en Mataró, Maresme y Barcelona
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              <div className="relative w-full h-96 lg:h-[500px] rounded-xl overflow-hidden border-2 border-[#FF007A]/20">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d47616.89842284937!2d2.4147!3d41.5402!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12a4bc9c5e5e5e5e%3A0x5e5e5e5e5e5e5e5e!2zTWF0YXLDsywgQmFyY2Vsb25h!5e0!3m2!1ses!2ses!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Mapa de servicio de pladur en Mataró"
                ></iframe>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-4"
            >
              <div className="bg-[#12121A] rounded-xl p-4 sm:p-6 border border-[#FF007A]/10">
                <div className="flex items-start mb-4">
                  <div className="w-12 h-12 bg-[#FF007A]/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-map-pin-2-fill text-2xl text-[#FF007A]" aria-hidden="true"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold mb-2 break-words">Mataró centro</h3>
                    <p className="text-sm text-[#F3F3F3]/70 break-words">Cobertura completa en todos los barrios de Mataró</p>
                  </div>
                </div>
                <ul className="space-y-2 text-sm text-[#F3F3F3]/70">
                  <li className="flex items-center">
                    <i className="ri-checkbox-circle-fill text-[#FF007A] mr-2 flex-shrink-0" aria-hidden="true"></i>
                    <span className="break-words">Centre y Eixample</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-checkbox-circle-fill text-[#FF007A] mr-2 flex-shrink-0" aria-hidden="true"></i>
                    <span className="break-words">Cerdanyola y Rocafonda</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-checkbox-circle-fill text-[#FF007A] mr-2 flex-shrink-0" aria-hidden="true"></i>
                    <span className="break-words">El Palau y Vista Alegre</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-checkbox-circle-fill text-[#FF007A] mr-2 flex-shrink-0" aria-hidden="true"></i>
                    <span className="break-words">Cirera y Peramàs</span>
                  </li>
                </ul>
              </div>

              <div className="bg-[#12121A] rounded-xl p-4 sm:p-6 border border-[#FF007A]/10">
                <div className="flex items-start mb-4">
                  <div className="w-12 h-12 bg-[#FF007A]/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-road-map-line text-2xl text-[#FF007A]" aria-hidden="true"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold mb-2 break-words">Comarca del Maresme</h3>
                    <p className="text-sm text-[#F3F3F3]/70 break-words">Servicio en toda la comarca</p>
                  </div>
                </div>
                <ul className="space-y-2 text-sm text-[#F3F3F3]/70">
                  <li className="flex items-center">
                    <i className="ri-checkbox-circle-fill text-[#FF007A] mr-2 flex-shrink-0" aria-hidden="true"></i>
                    <span className="break-words">Argentona y Cabrera de Mar</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-checkbox-circle-fill text-[#FF007A] mr-2 flex-shrink-0" aria-hidden="true"></i>
                    <span className="break-words">Vilassar y Premià de Mar</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-checkbox-circle-fill text-[#FF007A] mr-2 flex-shrink-0" aria-hidden="true"></i>
                    <span className="break-words">Arenys y Dosrius</span>
                  </li>
                </ul>
              </div>

              <div className="bg-[#12121A] rounded-xl p-4 sm:p-6 border border-[#FF007A]/10">
                <div className="flex items-start mb-4">
                  <div className="w-12 h-12 bg-[#FF007A]/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-building-2-line text-2xl text-[#FF007A]" aria-hidden="true"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold mb-2 break-words">Barcelona</h3>
                    <p className="text-sm text-[#F3F3F3]/70 break-words">Zonas seleccionadas</p>
                  </div>
                </div>
                <ul className="space-y-2 text-sm text-[#F3F3F3]/70">
                  <li className="flex items-center">
                    <i className="ri-checkbox-circle-fill text-[#FF007A] mr-2 flex-shrink-0" aria-hidden="true"></i>
                    <span className="break-words">Sant Martí</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-checkbox-circle-fill text-[#FF007A] mr-2 flex-shrink-0" aria-hidden="true"></i>
                    <span className="break-words">Eixample</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-r from-[#FF007A]/10 to-[#FF4DA6]/10 rounded-xl p-4 sm:p-6 border border-[#FF007A]/20">
                <p className="text-sm text-[#F3F3F3]/80 mb-4 break-words">
                  <i className="ri-information-line text-[#FF007A] mr-2" aria-hidden="true"></i>
                  ¿Tu zona no aparece? Consúltanos disponibilidad
                </p>
                <a
                  href="tel:+34629501941"
                  className="block w-full px-4 py-3 bg-[#FF007A] text-white rounded-lg text-sm font-semibold hover:bg-[#FF4DA6] transition-all duration-300 text-center whitespace-nowrap cursor-pointer"
                  aria-label="Consultar cobertura llamando al 629 501 941"
                >
                  <i className="ri-phone-line mr-2" aria-hidden="true"></i>
                  Consultar Cobertura
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0B0B0F]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 break-words">
              Preguntas <span className="text-[#FF007A]">frecuentes</span>
            </h2>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#12121A] rounded-lg p-6 border border-[#FF007A]/10"
              >
                <h3 className="text-lg font-semibold mb-3 text-[#FF007A]">{faq.question}</h3>
                <p className="text-[#F3F3F3]/70 text-sm leading-relaxed">{faq.answer}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link
              to="/faq"
              className="inline-block px-8 py-3 bg-[#FF007A] text-white rounded-lg font-semibold hover:bg-[#FF4DA6] transition-all duration-300 shadow-lg hover:shadow-[#FF007A]/50 whitespace-nowrap cursor-pointer"
            >
              Ver Todas las Preguntas
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-b from-[#12121A] to-[#0B0B0F]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              ¿Listo para tu <span className="text-[#FF007A]">reforma?</span>
            </h2>
            <p className="text-lg text-[#F3F3F3]/70 mb-8 max-w-2xl mx-auto break-words">
              Contacta con nosotros y recibe un presupuesto personalizado sin compromiso. Te asesoramos en todo el proceso.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="tel:+34629501941"
                className="px-8 py-4 bg-[#FF007A] text-white rounded-lg text-base font-semibold hover:bg-[#FF4DA6] transition-all duration-300 shadow-lg hover:shadow-[#FF007A]/50 whitespace-nowrap cursor-pointer"
                aria-label="Llamar a PLADUREX al 629 501 941"
              >
                <i className="ri-phone-line mr-2" aria-hidden="true"></i>
                629 501 941
              </a>
              <Link
                to="/contacto"
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-lg text-base font-semibold hover:bg-white/20 transition-all duration-300 border border-white/20 whitespace-nowrap cursor-pointer"
              >
                Formulario de Contacto
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
      <CookieBanner />
      <BackToTopButton />
    </div>
  );
};

export default HomePage;
